import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(5px);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: fadeIn 0.3s ease;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

const CardContainer = styled.div`
    position: relative;
    width: 100%;
    max-width: 400px;
    background: #1a1a3e;
    border-radius: 20px;
    padding: 40px 30px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    animation: scaleIn 0.3s ease;
    color: #f4d4a3;

    @keyframes scaleIn {
        from {
            transform: scale(0.9);
            opacity: 0;
        }
        to {
            transform: scale(1);
            opacity: 1;
        }
    }

    @media (max-width: 768px) {
        padding: 30px 20px;
        max-width: 90%;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 15px;
    right: 15px;
    background: transparent;
    border: none;
    color: #f4d4a3;
    font-size: 28px;
    cursor: pointer;
    width: 35px;
    height: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.3s ease;
    opacity: 0.7;

    &:hover {
        opacity: 1;
        background: rgba(244, 212, 163, 0.1);
        transform: rotate(90deg);
    }
`;

const TopChevrons = styled.div`
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 20px;
    opacity: 0.8;
`;

const Chevron = styled.div`
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 8px solid #f4d4a3;
`;

const TopText = styled.div`
    text-align: center;
    font-size: 0.7rem;
    letter-spacing: 2px;
    margin-bottom: 15px;
    opacity: 0.9;
    font-family: 'Arial', sans-serif;
`;

const TitleSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    gap: 10px;
`;

const TitleChevron = styled.div`
    width: 0;
    height: 0;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-left: 8px solid #f4d4a3;
`;

const TitleText = styled.div`
    font-size: 1rem;
    letter-spacing: 1px;
    font-family: 'Arial', sans-serif;
`;

const NameLarge = styled.div`
    font-size: 2.5rem;
    font-weight: bold;
    text-align: center;
    margin-bottom: 10px;
    letter-spacing: 3px;
    font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const NameSmall = styled.div`
    font-size: 1.2rem;
    text-align: center;
    margin-bottom: 30px;
    opacity: 0.9;
    letter-spacing: 2px;
    font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
`;

const InfoSection = styled.div`
    margin-bottom: 30px;
`;

const InfoItem = styled.div`
    margin-bottom: 15px;
    font-size: 0.9rem;
    line-height: 1.6;
    font-family: 'Arial', sans-serif;
`;

const InfoLabel = styled.span`
    opacity: 0.8;
    margin-right: 8px;
`;

const InfoValue = styled.span`
    opacity: 1;
`;

const ClickableValue = styled.span`
    opacity: 1;
    cursor: pointer;
    position: relative;
    padding: 6px 12px 6px 8px;
    border-radius: 6px;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid rgba(244, 212, 163, 0.2);
    background: rgba(244, 212, 163, 0.05);
    user-select: none;

    &:hover {
        background: rgba(244, 212, 163, 0.15);
        border-color: rgba(244, 212, 163, 0.5);
        transform: translateY(-2px);
        box-shadow: 0 2px 8px rgba(244, 212, 163, 0.2);
    }

    &:active {
        transform: translateY(0);
        background: rgba(244, 212, 163, 0.25);
    }

    /* Copy icon using CSS */
    &::before {
        content: '';
        width: 12px;
        height: 12px;
        display: inline-block;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23f4d4a3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='9' y='9' width='13' height='13' rx='2' ry='2'%3E%3C/rect%3E%3Cpath d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1'%3E%3C/path%3E%3C/svg%3E");
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        opacity: 0.7;
        transition: all 0.3s ease;
        flex-shrink: 0;
    }

    &:hover::before {
        opacity: 1;
        transform: scale(1.1);
    }

    ${props => props.copied && `
        border-color: rgba(76, 175, 80, 0.6);
        background: rgba(76, 175, 80, 0.15);
        
        &::before {
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%234caf50' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
            opacity: 1;
        }
    `}
`;

const BottomIllustration = styled.div`
    text-align: center;
    margin-top: 30px;
    opacity: 0.9;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
        max-width: 120px;
        width: 100%;
        height: auto;
        filter: drop-shadow(0 4px 12px rgba(244, 212, 163, 0.3));
        
        @media (max-width: 768px) {
            max-width: 100px;
        }
    }
`;

const BottomChevrons = styled.div`
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 20px;
    opacity: 0.8;
`;

const ChevronUp = styled.div`
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 8px solid #f4d4a3;
`;

const BusinessCard = ({ onClose }) => {
    const [copiedDC, setCopiedDC] = useState(false);
    const [copiedEmail, setCopiedEmail] = useState(false);

    const copyToClipboard = async (text, type) => {
        try {
            await navigator.clipboard.writeText(text);
            if (type === 'dc') {
                setCopiedDC(true);
                setTimeout(() => setCopiedDC(false), 2000);
            } else {
                setCopiedEmail(true);
                setTimeout(() => setCopiedEmail(false), 2000);
            }
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <Overlay onClick={onClose}>
            <CardContainer onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={onClose}>×</CloseButton>
                
                <TopChevrons>
                    <Chevron />
                    <Chevron />
                    <Chevron />
                </TopChevrons>

                <TopText>NIGHT WALKER GAMES</TopText>

                <TitleSection>
                    <TitleChevron />
                    <TitleText>第八席</TitleText>
                </TitleSection>

                <NameLarge>卡夫卡</NameLarge>
                <NameSmall>曾大祐</NameSmall>

                <InfoSection>
                    <InfoItem>
                        <InfoLabel>程式開發</InfoLabel>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>DC</InfoLabel>
                        <ClickableValue 
                            copied={copiedDC}
                            onClick={() => copyToClipboard('bigyo11255', 'dc')}
                        >
                            bigyo1125
                        </ClickableValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>信箱</InfoLabel>
                        <ClickableValue 
                            copied={copiedEmail}
                            onClick={() => copyToClipboard('alan25799@gmail.com', 'email')}
                        >
                            alan25799@gmail.com
                        </ClickableValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>喜歡的食物</InfoLabel>
                        <InfoValue>拉麵、炒飯</InfoValue>
                    </InfoItem>
                </InfoSection>

                <BottomIllustration>
                    <img src="/images/creature.png" alt="Creature" />
                </BottomIllustration>

                <BottomChevrons>
                    <ChevronUp />
                    <ChevronUp />
                    <ChevronUp />
                </BottomChevrons>
            </CardContainer>
        </Overlay>
    );
};

export default BusinessCard;

